/**
 * Mi cuenta: datos del operador conectado y alta del segundo factor.
 *
 * El API expone `POST /operators/totp/setup` y `/totp/verify` desde la fase 1, pero la consola no
 * tenía por dónde llamarlos: activar el segundo factor era imposible desde la interfaz, justo en
 * la cuenta que ve el padrón completo de organizaciones.
 *
 * No se dibuja un código QR a propósito: exigiría una dependencia nueva o un generador propio, y
 * todos los autenticadores admiten alta manual. Se muestra el secreto y el `otpauth://` completo.
 */
import type { Operator } from "@continnua/contracts";
import { useEffect, useState, type ReactElement } from "react";

import { operatorsApi } from "../api/client";
import { Badge } from "../components/Badge";
import { Field } from "../components/Field";
import { toDisplayMessage } from "../lib/errors";
import { formatDateTime } from "../lib/format";

/** El secreto viaja dentro del `otpauth://`; se extrae para poder ofrecer el alta manual. */
function secretoDe(otpauthUrl: string): string | null {
  try {
    return new URL(otpauthUrl).searchParams.get("secret");
  } catch {
    return null;
  }
}

export function Cuenta(): ReactElement {
  const [operador, setOperador] = useState<Operator | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let cancelado = false;
    operatorsApi
      .me()
      .then((o) => {
        if (!cancelado) setOperador(o);
      })
      .catch((err: unknown) => {
        if (!cancelado) setError(toDisplayMessage(err));
      });
    return () => {
      cancelado = true;
    };
  }, []);

  async function iniciarAlta(): Promise<void> {
    setError(null);
    setAviso(null);
    setOcupado(true);
    try {
      const { otpauthUrl: url } = await operatorsApi.totpSetup();
      setOtpauthUrl(url);
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    setError(null);
    setOcupado(true);
    try {
      const actualizado = await operatorsApi.totpVerify(codigo.trim());
      setOperador(actualizado);
      setOtpauthUrl(null);
      setCodigo("");
      setAviso("Segundo factor activado. A partir del próximo ingreso se pedirá el código.");
    } catch (err) {
      setError(toDisplayMessage(err));
    } finally {
      setOcupado(false);
    }
  }

  const secreto = otpauthUrl ? secretoDe(otpauthUrl) : null;

  return (
    <section>
      <div className="page-header">
        <h1>Mi cuenta</h1>
      </div>

      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {aviso && (
        <p className="notice" role="status">
          {aviso}
        </p>
      )}

      {operador && (
        <table className="data-table">
          <tbody>
            <tr>
              <th scope="row">Correo</th>
              <td>{operador.email}</td>
            </tr>
            <tr>
              <th scope="row">Nombre</th>
              <td>{operador.fullName}</td>
            </tr>
            <tr>
              <th scope="row">Rol</th>
              <td>{operador.role}</td>
            </tr>
            <tr>
              <th scope="row">Último ingreso</th>
              <td>{operador.lastLoginAt ? formatDateTime(operador.lastLoginAt) : "—"}</td>
            </tr>
            <tr>
              <th scope="row">Segundo factor</th>
              <td>
                {operador.totpEnabled ? (
                  <Badge tone="success">Activado</Badge>
                ) : (
                  <Badge tone="warning">Sin activar</Badge>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <h2>Segundo factor (TOTP)</h2>

      {operador?.totpEnabled && !otpauthUrl && (
        <p className="subtle">
          Ya está activado. Volver a darlo de alta reemplaza el anterior: hágalo solo si perdió el
          autenticador.
        </p>
      )}

      {!otpauthUrl && (
        <button type="button" className="button" onClick={() => void iniciarAlta()} disabled={ocupado}>
          {operador?.totpEnabled ? "Volver a dar de alta" : "Activar segundo factor"}
        </button>
      )}

      {otpauthUrl && (
        <div className="panel">
          <p>
            Dé de alta esta cuenta en su autenticador y escriba el primer código de seis dígitos. El
            secreto se muestra una sola vez: si cierra esta pantalla sin confirmar, hay que empezar
            de nuevo.
          </p>
          {secreto && (
            <p>
              <strong>Secreto:</strong> <code>{secreto}</code>
            </p>
          )}
          <p className="subtle">
            <code>{otpauthUrl}</code>
          </p>
          <form onSubmit={(e) => void confirmar(e)}>
            <Field label="Código de seis dígitos">
              {(props) => (
                <input
                  {...props}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={codigo}
                  onChange={(e) => setCodigo(e.target.value)}
                  required
                />
              )}
            </Field>
            <button type="submit" className="button" disabled={ocupado || codigo.trim().length !== 6}>
              Confirmar
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
